import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/utils/invokeEdgeFunction";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, Copy } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";

type Candidate = Tables<"candidates">;
const CUSTOM_PROMPT_OPTION = "__custom_prompt__";

type GeneratedQuestion =
  | {
      question: string;
      options: {
        A: string;
        B: string;
        C: string;
        D: string;
        correct_answer: string;
      };
    }
  | {
      question: string;
      ai_prompt: string;
    };

interface AIQuestionGeneratorProps {
  candidate: Candidate;
}

export function AIQuestionGenerator({ candidate }: AIQuestionGeneratorProps) {
  const { toast } = useToast();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [customPrompt, setCustomPrompt] = useState("");
  const [generatedQuestions, setGeneratedQuestions] = useState<
    GeneratedQuestion[]
  >([]);
  const [rawOutput, setRawOutput] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  // Fetch AI question templates
  const { data: templates = [] } = useQuery({
    queryKey: [
      "ai-question-templates",
      candidate.job_position_id,
      candidate.job_positions?.job_type,
      candidate.current_round,
    ],
    queryFn: async () => {
      let query = supabase
        .from("ai_question_templates")
        .select("*")
        .eq("is_active", true)
        .order("name");

      if (candidate.job_position_id) {
        query = query.or(
          `job_role_ids.cs.{${candidate.job_position_id}},job_role_ids.is.null,job_role_ids.eq.{}`
        );
      }
      if (candidate.job_positions?.job_type) {
        query = query.or(
          `job_role_types.cs.{${candidate.job_positions.job_type}},job_role_types.is.null,job_role_types.eq.{}`
        );
      }
      if (candidate.current_round) {
        query = query.or(
          `rounds.cs.{${candidate.current_round}},rounds.is.null,rounds.eq.{}`
        );
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    },
  });

  const handleGenerateQuestions = async () => {
    if (!selectedTemplateId) {
      toast({
        title: "Error",
        description: "Please select a question template first.",
        variant: "destructive",
      });
      return;
    }

    if (
      selectedTemplateId === CUSTOM_PROMPT_OPTION &&
      !customPrompt.trim()
    ) {
      toast({
        title: "Error",
        description: "Please enter a custom prompt first.",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    try {
      const data = await invokeEdgeFunction<any>(
        "generate-questions",
        {
          body: {
            templateId:
              selectedTemplateId === CUSTOM_PROMPT_OPTION
                ? null
                : selectedTemplateId,
            customPrompt:
              selectedTemplateId === CUSTOM_PROMPT_OPTION
                ? customPrompt.trim()
                : null,
            candidateData: {
              key_skills: candidate.key_skills,
              position_applied: candidate.position_applied,
              technologies: candidate.technologies,
              qualification: candidate.qualification,
              branch: candidate.branch,
              parsed_resume_data: candidate.parsed_resume_data,
              resume_markdown: candidate.resume_markdown,
            },
          },
        }
      );

      console.log({ data });

      let questions: GeneratedQuestion[] = [];
      let raw = "";
      const q = data.questions as any;
      if (Array.isArray(q)) {
        questions = q as GeneratedQuestion[];
      } else if (typeof q === "string") {
        try {
          const parsed = JSON.parse(q);
          const arr = Array.isArray(parsed.questions)
            ? parsed.questions
            : parsed;
          if (Array.isArray(arr)) {
            questions = arr as GeneratedQuestion[];
          } else {
            raw = q;
          }
        } catch {
          raw = q;
        }
      } else if (q && typeof q === "object") {
        const arr = Array.isArray(q.questions) ? q.questions : q;
        if (Array.isArray(arr)) {
          questions = arr as GeneratedQuestion[];
        } else {
          raw = JSON.stringify(q);
        }
      }

      setGeneratedQuestions(questions);
      setRawOutput(raw);
      toast({
        title: "Questions Generated",
        description: "AI has generated interview questions for this candidate.",
      });
    } catch (error) {
      console.error("Error generating questions:", error);
      toast({
        title: "Error",
        description: "Failed to generate questions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyQuestions = () => {
    const text = generatedQuestions.length
      ? JSON.stringify(generatedQuestions, null, 2)
      : rawOutput;
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "Questions copied to clipboard.",
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          Generate AI Questions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="template">Question Template</Label>
          <Select
            onValueChange={setSelectedTemplateId}
            value={selectedTemplateId}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select question type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CUSTOM_PROMPT_OPTION}>Custom Prompt</SelectItem>
              {templates.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedTemplateId && (
            <p className="text-sm text-muted-foreground mt-1">
              {selectedTemplateId === CUSTOM_PROMPT_OPTION
                ? "Provide detailed instructions for the AI. The candidate CV will still be passed for context, and the response will be forced into the same structured question format."
                : templates.find((t) => t.id === selectedTemplateId)?.description}
            </p>
          )}
        </div>

        {selectedTemplateId === CUSTOM_PROMPT_OPTION && (
          <div>
            <Label htmlFor="custom-prompt">Custom Prompt</Label>
            <Textarea
              id="custom-prompt"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              rows={6}
              placeholder="Example: Generate 5 role-specific JavaScript debugging questions in structured JSON format, with medium difficulty and practical scenarios based on the candidate's experience."
            />
          </div>
        )}

        <Button
          onClick={handleGenerateQuestions}
          disabled={
            isGenerating ||
            !selectedTemplateId ||
            (selectedTemplateId === CUSTOM_PROMPT_OPTION && !customPrompt.trim())
          }
          className="w-full"
        >
          {isGenerating ? "Generating..." : "Generate Questions"}
        </Button>

        {generatedQuestions.length > 0 ? (
          <div className="space-y-4">
            {generatedQuestions.map((q, index) => (
              <div key={index} className="border rounded p-4 space-y-2">
                <p className="font-medium">
                  {index + 1}. {q.question}
                </p>
                {"options" in q ? (
                  <div className="space-y-1">
                    {(["A", "B", "C", "D"] as const).map((opt) => (
                      <div key={opt} className="text-sm flex gap-2">
                        <span className="font-semibold">{opt}.</span>
                        {(q.options as any)[opt]}
                      </div>
                    ))}
                    <div className="text-sm text-green-600">
                      Correct answer: {(q.options as any).correct_answer}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {(q as any).ai_prompt}
                  </p>
                )}
              </div>
            ))}
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyQuestions}
                className="flex items-center gap-1"
              >
                <Copy className="h-3 w-3" />
                Copy
              </Button>
            </div>
          </div>
        ) : (
          rawOutput && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Generated Questions</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyQuestions}
                  className="flex items-center gap-1"
                >
                  <Copy className="h-3 w-3" />
                  Copy
                </Button>
              </div>
              <Textarea
                value={rawOutput}
                onChange={(e) => setRawOutput(e.target.value)}
                rows={8}
                className="resize-none"
                placeholder="Generated questions will appear here..."
              />
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
}
